import { useEffect, useState } from 'react';

import {
  ApiError,
  formatPrice,
  getCheckoutContext,
  startCheckout,
  type CheckoutContext,
} from '../../lib/api.js';
import './begin.css';

/**
 * The start of a purchase: what you are buying, who you are, and the acknowledgements before money.
 *
 * This is where a visitor lands from the Framer site — `/begin?plan=remote-chart`. The plan and the
 * exact consent texts come from the API, so the price shown is the server's price and the
 * acknowledgements are the versions in force. Nothing about the amount is taken from the URL.
 *
 * The button is disabled until every required box is ticked, but that disabling decides nothing: the
 * API records each acknowledgement, verifies each checksum, and refuses to create a Checkout Session
 * without all three (409). The disabled button is a courtesy, the 409 is the gate.
 */


export function Begin({ planSlug }: { planSlug: string }) {
  const [context, setContext] = useState<CheckoutContext | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [email, setEmail] = useState('');
  const [accepted, setAccepted] = useState<Record<string, boolean>>({});

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Set when the email already has an account — the way forward is to sign in, not pay again.
  const [alreadyHasAccount, setAlreadyHasAccount] = useState(false);

  useEffect(() => {
    let alive = true;
    getCheckoutContext(planSlug)
      .then((data) => {
        if (alive) setContext(data);
      })
      .catch((error: unknown) => {
        if (alive)
          setLoadError(
            error instanceof ApiError && error.status === 404
              ? 'We could not find that plan.'
              : 'Something went wrong loading this. Please try again in a moment.',
          );
      });
    return () => {
      alive = false;
    };
  }, [planSlug]);

  if (loadError) return <Centered>{loadError}</Centered>;
  if (!context) return <Centered>Loading…</Centered>;

  const allAccepted = context.consents.every((consent) => accepted[consent.gate] === true);
  const ready = email.trim() !== '' && allAccepted && !submitting;

  const submit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    setAlreadyHasAccount(false);
    try {
      // The email is checked before payment: if it already has an account, the server refuses here
      // and nothing is charged. The name and password come after payment.
      const result = await startCheckout({
        planSlug,
        email: email.trim(),
        consents: context.consents.map((consent) => ({
          gate: consent.gate,
          documentId: consent.documentId,
          version: consent.version,
          sha256: consent.sha256,
          accepted: true,
          context: { locale: 'en', scrolledToEnd: true },
        })),
      });
      // Leave the SPA entirely — Stripe hosts the payment page (SAQ A).
      window.location.assign(result.checkoutUrl);
    } catch (error) {
      if (error instanceof ApiError && error.code === 'CONFLICT') {
        setAlreadyHasAccount(true);
        setSubmitError('This email already has an account — no payment was taken. Please sign in.');
      } else {
        setSubmitError(
          error instanceof ApiError ? error.message : 'We could not start checkout. Please try again.',
        );
      }
      setSubmitting(false);
    }
  };

  return (
    <div className="begin">
      <div className="begin__plan">
        <p className="begin__eyebrow">You are beginning</p>
        <h1 className="begin__name">{context.plan.name}</h1>
        <p className="begin__summary">{context.plan.summary}</p>
        <p className="begin__price">
          {formatPrice(context.plan.amountMinor, context.plan.currency)}
          <span className="begin__tax"> · tax included</span>
        </p>
        <p className="begin__turnaround">
          Your reflection is prepared within {context.plan.turnaroundDays} days of your submission.
        </p>
      </div>

      <div className="begin__form">
        <label className="begin__field">
          <span className="begin__label">Your email</span>
          <input
            className="begin__input"
            type="email"
            value={email}
            autoComplete="email"
            onChange={(event) => {
              setEmail(event.target.value);
              setAlreadyHasAccount(false);
            }}
          />
          <span className="begin__hint">
            This is how you will sign in. Your receipt is sent here too.
          </span>
        </label>

        <p className="begin__lead">
          Please read and agree to each of these before payment. Your name and password come
          afterwards.
        </p>

        <div className="begin__consents">
          {context.consents.map((consent) => (
            <div className="begin__consent" key={consent.gate}>
              <label className="begin__tick">
                <input
                  type="checkbox"
                  checked={accepted[consent.gate] === true}
                  onChange={(event) =>
                    setAccepted((current) => ({ ...current, [consent.gate]: event.target.checked }))
                  }
                />
                <span>{consent.acknowledgement}</span>
              </label>
              <details className="begin__terms">
                <summary>Read this in full</summary>
                {consent.bodyMd.split('\n\n').map((paragraph) => (
                  <p key={paragraph.slice(0, 24)}>{paragraph}</p>
                ))}
              </details>
            </div>
          ))}
        </div>

        {submitError ? <p className="begin__error">{submitError}</p> : null}

        {alreadyHasAccount ? (
          <a className="button button--primary begin__submit" href="/login">
            Sign in
          </a>
        ) : (
          <button
            type="button"
            className="button button--primary begin__submit"
            disabled={!ready}
            onClick={() => void submit()}
          >
            {submitting ? 'Taking you to payment…' : 'Continue to payment'}
          </button>
        )}

        <p className="begin__secure">
          Payment is handled by Stripe. We never see or store your card details.
        </p>
      </div>
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return <div className="begin__centered">{children}</div>;
}
